/* istanbul ignore file */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Center, Flex } from '@chakra-ui/react';
import { FormType, SystemTranslationKeys } from '@common/entities';
import { useQuery } from '@tanstack/react-query';
import { NotificationStatus } from 'enums/notification-status';
import { RootState } from 'state/store';
import { useSelector } from 'state/storeHooks';

import { Loader } from 'components/Loader';
import notification from 'components/notification';
import { RenderRoute } from 'components/render-route/RenderRoute';
import { SelectApplication } from 'components/select-application/SelectApplication';
import { useCreateNewApplicationForUser, useSystemTranslation } from 'hooks';
import { getPropertyApplications } from 'services';

function SelectNewApplicationPage() {
  const { t, tSystem } = useSystemTranslation();
  const [newApplicationId, setNewApplicationId] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { email } = useSelector((state: RootState) => state.application.applicant);

  const createNewApplication = useCreateNewApplicationForUser();

  const { orgId: orgIdString = '', propertyId: propertyIdString = '' } = useParams();
  const propertyId = parseInt(propertyIdString, 10);
  const orgId = parseInt(orgIdString, 10);

  const { data: applications, isPending } = useQuery({
    queryKey: ['select-application-property-applications'],
    queryFn: () => getPropertyApplications(orgId, propertyId),
  });

  const applicationSelected = async (applicationId?: string) => {
    // Prefer caller-supplied id; state may not have flushed from RenderRoute (DEV-284170).
    const idToUse = applicationId || newApplicationId;
    setIsLoading(true);
    try {
      await createNewApplication(orgId, propertyId, idToUse, email);
    } catch {
      notification(
        t(
          SystemTranslationKeys.error.initializingApplication,
          'Error while initializing application.',
        ),
        NotificationStatus.ERROR,
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isPending)
    return <Loader label={tSystem(SystemTranslationKeys.common.loadingApplications)} />;
  if (isLoading)
    return (
      <Loader
        label={t(
          SystemTranslationKeys.common.initializingApplication,
          'Initializing new application...',
        )}
      />
    );

  return (
    <Flex bg="gray.highlighthalf" h="100%" flexDirection="column">
      <Center flex="1" bg="gray.highlighthalf">
        <Card
          p={6}
          maxW="700px"
          w="100%"
          h={{
            base: '100%',
            md: 'auto',
          }}
          my={8}
          key={newApplicationId}
        >
          {applications?.data?.length === 1 && applications?.data[0].type === FormType.Route ? (
            <RenderRoute
              route={applications.data[0]}
              applicationSelected={applicationSelected}
              setSelectedApplicationId={setNewApplicationId}
              setShowLoginLink={() => {}}
            />
          ) : (
            <SelectApplication
              applicationSelected={applicationSelected}
              setSelectedApplicationId={setNewApplicationId}
              currentApplicationId={newApplicationId}
            />
          )}
        </Card>
      </Center>
    </Flex>
  );
}

export default SelectNewApplicationPage;
